import jrpc20WSClient from "./jrpc20WSClient";
import employee from "./employee";

// Servizio Magazzino Packaging: espone i webservice pubblici warehouse/packagingWarehouse.
// Ogni chiamata invia { user_data:{token}, call_data:{...} } e risolve result.call_data.
// L'operatore non è mai un parametro: il BE lo ricava dal token (magazziniere loggato).
//
// QUANTITÀ "PER COLLO" — contratto valido per tutte le mutazioni:
//  - sul bucket INTERI si inviano N COLLI + PEZZI PER COLLO (`qty_per_collo_whole`); i pezzi
//    totali li DERIVA il BE (colli × pezzi/collo) e non sono più un input;
//  - `qty_per_collo_whole` fa parte della CHIAVE della riga di giacenza
//    (posizione + divisione + codice + lotto + pezzatura): pezzature diverse = righe diverse,
//    quindi è anche il selettore della riga che si sta alimentando;
//  - un COLLO APERTO è una RIGA a sé (qty_colli_partial sempre 1) con `partial_of_uuid` = riga
//    interi di provenienza, e `qty_pieces_partial` è il residuo di QUEL collo. In scrittura i colli
//    aperti si inviano in `opens` (un elemento per collo); in CONSULTAZIONE sono invece aggregati
//    nella riga dei loro colli interi.
class packagingWarehouse {
    wsClient = new jrpc20WSClient(process.env.REACT_APP_BACKEND_WS_URL, process.env.REACT_APP_BACKEND_WS_USR, process.env.REACT_APP_BACKEND_WS_PWD)

    _call(method, call_data) {
        const token = new employee().token;
        const post_data = { user_data: { token }, call_data: call_data || {} };
        return new Promise((resolve, reject) => {
            this.wsClient.request('post', 'warehouse/packagingWarehouse/' + method, post_data)
                .then((result) => resolve(result.call_data))
                .catch((error) => reject(error));
        });
    }

    // ── Letture ──
    // scope 'rientro' = include anche gli ordini EVASI (eccedenze di lavorazioni concluse).
    // Senza scope resta ai soli prelevabili (1,2,3), dove un evaso verrebbe rifiutato dal guard.
    getOrders(scope) { return this._call('wsGetOrders', scope ? { scope } : {}); }
    getDivisionCatalog() { return this._call('wsGetDivisionCatalog', {}); }   // { customers, divisions }
    getComponents() { return this._call('wsGetComponents', {}); }              // anagrafica completa
    getPositions() { return this._call('wsGetPositions', {}); }                // anagrafica posizioni
    getComponentsForDivision(division_uuid) { return this._call('wsGetComponentsForDivision', { division_uuid }); }
    // Articoli con giacenza, con filtri OPZIONALI: la consultazione per articolo non richiede la
    // divisione (si può cercare un codice in tutto il magazzino).
    // filters: { division_uuid?, customer_uuid? }
    getComponentsWithStock(filters) { return this._call('wsGetComponentsForDivision', filters || {}); }
    // → { component, rows:[ { …, qty_colli_whole, qty_per_collo_whole(+_display), qty_pieces_whole,
    //                         qty_colli_partial, qty_pieces_partial, qty_total(+_display) } ] }
    getPositionsByCode(division_uuid, code) { return this._call('wsGetPositionsByCode', { division_uuid, code }); }
    // Ricerca giacenze a filtri opzionali (Consultazione: per articolo / per posizione / tutto il magazzino).
    // filters: { division_uuid?, customer_uuid?, component_uuid?, code?, lotto?,
    //            area?, scaffale?, posizione?, piano?, limit?, offset? }
    // → { rows, total, limit, offset, totals:{qty_total,qty_colli_whole,qty_colli_partial} }
    // Le righe sono AGGREGATE per gruppo (posizione+divisione+codice+lotto+pezzatura): i colli aperti
    // si sommano in `partials_count`/`partials_pieces` e il dettaglio si chiede con getGroupPartials.
    // `total` è quindi il numero di GRUPPI, non di righe di tabella.
    // NB: `totals` NON contiene la pezzatura — un formato per-unità non si somma.
    searchStock(filters) { return this._call('wsSearchStock', filters || {}); }
    // Dettaglio dei singoli COLLI APERTI di un gruppo di consultazione (searchStock li aggrega).
    // → { partials: [ { stock_uuid, qty_pieces_partial(+_display), qty_per_collo_whole, created_at, note } ] }
    getGroupPartials(key) { return this._call('wsGetGroupPartials', key); }
    // → { location: { position_uuid, lotto, qty_per_collo_whole } } dal prelievo più recente.
    // La pezzatura serve al Rientro: posizione+lotto identificano una FAMIGLIA di righe, non una riga.
    getOrderPickLocation(order_uuid, component_uuid) { return this._call('wsGetOrderPickLocation', { order_uuid, component_uuid }); }
    getOrderPickSummary(order_uuid) { return this._call('wsGetOrderPickSummary', { order_uuid }); }
    getOrderMovements(order_uuid) { return this._call('wsGetOrderMovements', { order_uuid }); }
    getStockMovements(stock_uuid) { return this._call('wsGetStockMovements', { stock_uuid }); }
    // Storico di un ARTICOLO in una divisione e un lotto. Scala VOLUTAMENTE più larga della riga di
    // consultazione da cui si apre: posizione e pezzatura NON entrano, perché il materiale cambia
    // cella e la pezzatura spezza le righe, quindi la storia di un materiale fisico risultava a
    // frammenti. Comprende i colli aperti, anche quelli esauriti.
    // key: { division_uuid, component_uuid, lotto?, filters? } — `filters` come in getAllMovements,
    // in AND con la scala dell'articolo (codice e lotto li fissa già la scala: non si mandano).
    getArticleMovements(key) { return this._call('wsGetArticleMovements', key); }
    // Storico generale del magazzino (ultimi movimenti), filtrabile.
    // filters: { movement_type:[...], movement_date:{from,to}, component_code, component_description,
    //            position_cella, division_name, lotto, order_production_number }
    // I filtri si applicano in SQL, non a valle del limit (filtrare dopo cercherebbe solo negli
    // ultimi N movimenti). L'operatore non è filtrabile: è cifrato AES a riposo.
    getAllMovements(limit, filters) { return this._call('wsGetAllMovements', { limit, filters: filters || {} }); }

    // ── Mutazioni ── (operatore = token; nessun operator_uuid inviato)
    // { order_uuid, client_operation_id, picks: [
    //     { stock_uuid, bucket:'whole',   colli_full, opens:[{pieces_picked}, ...] },
    //     { stock_uuid, bucket:'partial', pieces_picked } ] }
    // Bucket 'whole': i pezzi dei colli presi interi li deriva il BE dalla pezzatura (si inviano
    // solo i colli). `opens` ha UN ELEMENTO PER COLLO da aprire, con i pezzi che se ne prelevano:
    // ciascuno diventa una riga col SUO residuo. Gli scalari `pieces_picked`/`pieces_picked_open`
    // sul bucket 'whole' sono RIFIUTATI dal BE (un client vecchio fallisce chiuso).
    pickMaterial(data) { return this._call('wsPickMaterial', data); }
    // { division_uuid, position_uuid, component_uuid, lotto, qty_colli_whole,
    //   qty_per_collo_whole, opens:[{pieces}, ...], note, client_operation_id }
    // `opens` ha un elemento per COLLO APERTO, ciascuno coi suoi pezzi residui: ognuno diventa una
    // riga a sé. Gli scalari qty_colli_partial/qty_pieces_partial sono RIFIUTATI dal BE.
    placeMaterial(data) { return this._call('wsPlaceMaterial', data); }      // posizionamento
    // Come placeMaterial ma con order_uuid al posto di division_uuid (divisione ricavata dall'ordine).
    returnLeftover(data) { return this._call('wsReturnLeftover', data); }    // rientro
    // Riga INTERI:       { stock_uuid, new_position_uuid, new_lotto, new_qty_colli_whole, new_qty_per_collo_whole, note }
    // Riga COLLO APERTO: { stock_uuid, new_qty_pieces_partial (0 = collo finito), note } — la
    //   collocazione segue i colli interi, quindi non è modificabile se il parent è attivo.
    correctStock(data) { return this._call('wsCorrectStock', data); }        // correzione
}

export default packagingWarehouse;
